/**
 * Âge des comptes fournisseurs / clients — sommaire par tranche + détail des factures impayées.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { money } from "../_shared";
import { Card, Num, TH, THead, Table } from "./_bits";

const BUCKET_CLS = { current: "", d31_60: "text-amber-700", d61_90: "text-orange-700", over_90: "text-rose-700 font-semibold" };

function Detail({ g, labels }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border rounded-xl overflow-hidden" data-testid={`aging-group-${g.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full px-4 py-2 flex items-center justify-between gap-2 text-left hover:bg-slate-50" data-testid={`aging-toggle-${g.id}`}>
        <span className="flex items-center gap-2 text-sm"><span className="text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span><span className="font-semibold text-slate-800">{g.number && <span className="font-mono text-emerald-700 mr-1.5">{g.number}</span>}{g.name}</span><span className="text-xs text-slate-400">({g.items.length} facture{g.items.length > 1 ? "s" : ""})</span></span>
        <span className="font-mono text-sm font-semibold tabular-nums">{money(g.totals.total)}</span>
      </button>
      {open && (
        <Table>
          <THead><TH className="w-24">Date</TH><TH className="w-24">Échéance</TH><TH className="w-20">Réf.</TH><TH>Description</TH><TH right className="w-16">Jours</TH><TH className="w-32">Tranche</TH><TH right className="w-28">Montant</TH></THead>
          <tbody className="divide-y border-t">
            {g.items.map((it, i) => (
              <tr key={i} className="hover:bg-emerald-50/40">
                <td className="px-3 py-1.5 font-mono text-xs">{it.date}</td><td className="px-3 py-1.5 font-mono text-xs text-slate-500">{it.due_date || "—"}</td>
                <td className="px-3 py-1.5 text-xs text-slate-500">{it.ref || "—"}</td><td className="px-3 py-1.5 truncate max-w-md">{it.description}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{it.days}</td>
                <td className={`px-3 py-1.5 text-xs ${BUCKET_CLS[it.bucket]}`}>{labels[it.bucket]}</td>
                <Num v={it.amount} blankZero={false} />
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default function AgingReport({ data }) {
  const who = data.kind === "supplier" ? "fournisseurs" : "clients";
  return (
    <div className="space-y-4" data-testid={`report-${data.kind}-aging`}>
      <Card title={`Âge des comptes ${who} au ${data.as_of}`} right="Âge calculé selon la date de facture">
        <div className="overflow-x-auto">
          <Table>
            <THead><TH>{data.kind === "supplier" ? "Fournisseur" : "Client"}</TH>{data.buckets.map((b) => <TH key={b} right className="w-32">{data.bucket_labels[b]}</TH>)}<TH right className="w-32">Total</TH></THead>
            <tbody className="divide-y">
              {data.groups.map((g) => (
                <tr key={g.id} className="hover:bg-emerald-50/40" data-testid={`aging-row-${g.id}`}>
                  <td className="px-3 py-1.5 font-medium">{g.number && <span className="font-mono text-emerald-700 mr-1.5" data-testid={`aging-number-${g.id}`}>{g.number}</span>}{g.name}</td>
                  {data.buckets.map((b) => <Num key={b} v={g.totals[b]} className={BUCKET_CLS[b]} />)}
                  <Num v={g.totals.total} blankZero={false} className="font-semibold" />
                </tr>
              ))}
              {!data.groups.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-sm">Aucun solde impayé à cette date.</td></tr>}
              <tr className="bg-slate-50 font-bold">
                <td className="px-3 py-2 uppercase text-xs">Total</td>
                {data.buckets.map((b) => <Num key={b} v={data.totals[b]} blankZero={false} testId={`aging-total-${b}`} />)}
                <Num v={data.totals.total} blankZero={false} testId="aging-total" />
              </tr>
            </tbody>
          </Table>
        </div>
      </Card>
      {data.groups.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600">Détail des factures impayées</h4>
          {data.groups.map((g) => <Detail key={g.id} g={g} labels={data.bucket_labels} />)}
        </div>
      )}
    </div>
  );
}
